import { useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, CircularProgress } from '@mui/material';
import { useNotification } from '../../notifications/hooks/useNotification';
import { usePermissions } from '../../permissions/usePermissions';
import { useAuth } from '../../auth/hooks/useAuth';
import { useRightsProfiles } from '../hooks/useRightsProfiles';
import { useRightsProfilesCatalogue } from '../hooks/useRightsProfilesCatalogue';
import {
  RightsProfileForm,
  type RightsProfileFormSubmit,
} from '../components/RightsProfileForm/RightsProfileForm';
import { buildDefaultMatrix } from '../logic/permissionMatrix.logic';
import { ProfileNameConflictError } from '../errors/profile-conflict.error';

const USER_RIGHTS_LIST = '/my-ngo/user-rights';

/**
 * Create page (T5): fetches the permission catalogue, seeds the shared form with
 * the default matrix (display-only everywhere). On submit → create → success toast
 * → back to the list.
 */
export function CreateRightsProfilePage() {
  const { t } = useTranslation('rightsProfiles');
  const navigate = useNavigate();
  const { notifySuccess } = useNotification();
  const { can } = usePermissions();
  const { isInitialized } = useAuth();
  const { create, saving } = useRightsProfiles();
  const { catalogue, loading, error } = useRightsProfilesCatalogue();
  const [nameError, setNameError] = useState<string | undefined>(undefined);

  const initialMatrix = useMemo(
    () => (catalogue ? buildDefaultMatrix(catalogue) : null),
    [catalogue],
  );

  const handleSubmit = async (values: RightsProfileFormSubmit) => {
    setNameError(undefined);
    try {
      const created = await create({
        name: values.name,
        description: values.description || null,
        permissions: values.permissions,
      });
      if (created) {
        notifySuccess(t('create.success'));
        navigate(USER_RIGHTS_LIST);
      }
    } catch (err) {
      // A taken name (409, rule P2) is shown on the Name field, inputs preserved —
      // not a toast. Any other failure already raised a toast in the hook.
      if (ProfileNameConflictError.is(err)) {
        setNameError(t('form.nameConflict'));
        return;
      }
      throw err;
    }
  };

  // Guard direct navigation: the backend refuses POST /rights-profiles without
  // rights_profiles.create. Wait for the rights to load (`isLoaded`) before deciding,
  // then send a user who cannot create back to the list.
  // Rights load as part of the auth bootstrap, so once `isInitialized` they have
  // SETTLED (success or failure). A failed /auth/me leaves permissions null → can()
  // false → we send the user back to the list instead of hanging on a spinner that
  // waits for an `isLoaded` flag which never flips after an error.
  if (isInitialized && !can('rights_profiles', 'create')) {
    return <Navigate to={USER_RIGHTS_LIST} replace />;
  }

  if (loading || !isInitialized) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !catalogue || !initialMatrix) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{t('edit.loadError')}</Alert>
      </Box>
    );
  }

  return (
    <RightsProfileForm
      title={t('create.title')}
      submitLabel={t('form.create')}
      catalogue={catalogue}
      initialMatrix={initialMatrix}
      nameError={nameError}
      saving={saving}
      onSubmit={handleSubmit}
      onCancel={() => navigate(USER_RIGHTS_LIST)}
    />
  );
}
